<template>
  <a-modal
    :visible="visible"
    :title="id ? $t('modal.editTitle') : $t('modal.createTitle')"
    :destroy-on-close="true"
    :mask-closable="false"
    :closable="false"
    :width="'60%'"
    @cancel="handleCancel()"
  >
    <template slot="footer">
      <a-button key="cancel" @click="handleCancel">
        {{ $t('modal.cancel') }}
      </a-button>
      <a-button key="submit" type="primary" :disabled="loading" :loading="loading" @click="handleSubmit">
        {{ id ? $t('modal.update') : $t('modal.create') }}
      </a-button>
    </template>
    <a-spin :spinning="loading">
      <div class="spin-content setting-modal">
        <a-form :form="form" @submit="handleSubmit">
          <a-row>
            <a-col :md="11" :xs="24">
              <a-form-item :label="$t('userManager.column.name')">
                <a-input
                  v-decorator="[
                    'name',
                    { initialValue: data ? data.name : '' }
                  ]"
                  maxlength="255"
                />
              </a-form-item>
            </a-col>
            <a-col :md="{span: 11, offset: 2}" :xs="24">
              <a-form-item :label="$t('userManager.column.email')">
                <a-input
                  v-decorator="[
                    'email',
                    {
                      initialValue: data ? data.email : '',
                      rules: [
                        { required: true, message: $t('userManager.validations.emailRequired') },
                        { type: 'email', message: $t('userManager.validations.emailInvalid') },
                      ],
                    }
                  ]"
                  maxlength="255"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :md="11" :xs="24">
              <a-form-item :label="$t('userManager.column.telephone')">
                <a-input
                  v-decorator="[
                    'tel',
                    { initialValue: data ? data.tel : '' }
                  ]"
                  maxlength="255"
                />
              </a-form-item>
            </a-col>
            <a-col :md="{span: 11, offset: 2}" :xs="24">
              <a-form-item :label="$t('userManager.column.birthday')">
                <a-date-picker
                  v-decorator="[
                    'birthday',
                    { initialValue: data ? data.birthday : '' }
                  ]"
                  style="width: 100%"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :md="11" :xs="24">
              <a-form-item :label="$t('userManager.column.password')">
                <a-input
                  v-decorator="[
                    'password',
                    {
                      initialValue: null,
                      rules: [
                        { required: !id, message: $t('userManager.validations.passwordRequired') },
                        { validator: handleInvalidPassword }
                      ],
                    }
                  ]"
                  type="password"
                  maxlength="255"
                  minlength="8"
                  @change="handlePasswordChange"
                />
              </a-form-item>
            </a-col>
            <a-col :md="{span: 11, offset: 2}" :xs="24">
              <a-form-item :label="$t('userManager.column.passwordConfirm')">
                <a-input
                  v-decorator="[
                    'passwordConfirm',
                    {
                      initialValue: null,
                      rules: [
                        { required: !id, message: $t('userManager.validations.passwordConfirmRequired') },
                        { validator: compareToPassword }
                      ],
                    }
                  ]"
                  type="password"
                  maxlength="255"
                  minlength="8"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import {
  StoreUser as ApiStoreUser,
  ShowUser as ApiShowUser,
  UpdateUser as ApiUpdateUser
} from '@/api/users';

export default {
  name: 'UserModal',
  data() {
    return {
      // roleId: roleId,
      visible: false,
      form: this.$form.createForm(this),
      id: null,
      data: null,
      loading: false
    };
  },
  methods: {
    getUser() {
      this.loading = true;
      ApiShowUser({ id: this.id })
        .then((response) => {
          this.data = response.data;
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    showModal(id = null) {
      this.visible = true;
      this.id = id;
      this.clearData();
      if (id) {
        this.getUser();
      }
    },
    handleSubmit(e) {
      e.preventDefault();
      this.form.validateFields({ force: true }, (err, values) => {
        if (!err) {
          delete values['passwordConfirm'];
          if (values.password == null) {
            delete values['password'];
          }
          const apiName = this.id ? ApiUpdateUser : ApiStoreUser;
          if (this.id) {
            values.id = this.id;
          }
          this.loading = true;
          apiName(values)
            .then(() => {
              this.loading = false;
              this.visible = false;
              this.$notification.success({ message: this.$t(`modal.successMessage.${this.id ? 'updateSuccess' : 'createSuccess'}`) });
              this.$emit('updateUserSuccess');
            })
            .catch((error) => {
              // Handle API status code
              if (error.response.status !== 200) {
                const { message } = error.response.data;
                // Show error message
                this.$error({
                  title: this.$t('modal.failModal.title'),
                  content: message,
                  okText: this.$t('modal.failModal.close')
                });
              }
              this.loading = false;
            });
        }
      });
    },
    handleCancel() {
      this.visible = false;
    },
    compareToPassword(rule, value, callback) {
      const password = this.form.getFieldValue('password');
      if (password && value !== password) {
        callback(this.$t('userManager.validations.passwordConfirmNotMatch'));
      } else {
        callback();
      }
    },
    handleInvalidPassword(rule, value, callback) {
      const password = this.form.getFieldValue('password');
      if (password && !password.trim()) {
        callback(this.$t('userManager.validations.passwordInvalid'));
      } else {
        callback();
      }
    },
    handlePasswordChange(password) {
      // Validate dependent field in case the rule is changed but the field isn't dirty
      this.form.validateFields(['passwordConfirm'], { force: true });
    },
    clearData() {
      this.form.resetFields();
    }
  }
};
</script>

<style lang="scss">
    .setting-modal {
        .ant-form-item {
            margin: 0 0 15px !important;
        }
        .ant-form-item-label {
            line-height: 25px !important;
        }
    }
</style>
